import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Mail, MailCheck, ShieldCheck } from "lucide-react";
import SiteLogo from "../../components/SiteLogo";
import PageSeo from "../../components/PageSeo";
import old1_PublicSiteFooter from "../components/old1_PublicSiteFooter";
import JobuwinPageLoading from "../../components/JobuwinPageLoading";
import TurnstileField from "../../components/TurnstileField";
import {
  isTurnstileEnabled,
  TURNSTILE_REQUIRED_MESSAGE,
  formatTurnstileErrorForUser,
} from "../../turnstileConfig";
import {
  isApiConfigured,
  getEmailChangeStatus,
  postEmailChangeNewEmail,
  postEmailChangeResend,
  postEmailChangeVerify,
} from "../../apiClient";

function old1_ChangeEmailPage({ onSignIn, authToken }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = useMemo(() => {
    const raw = searchParams.get("token");
    return raw ? String(raw).trim() : "";
  }, [searchParams]);

  const [loadingStatus, setLoadingStatus] = useState(true);
  const [status, setStatus] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [submittingNew, setSubmittingNew] = useState(false);
  const [submittingVerify, setSubmittingVerify] = useState(false);
  const [resendStatus, setResendStatus] = useState("");
  const [done, setDone] = useState(false);
  const [newEmailTurnstileToken, setNewEmailTurnstileToken] = useState(null);
  const [newEmailTurnstileKey, setNewEmailTurnstileKey] = useState(0);
  const [verifyTurnstileToken, setVerifyTurnstileToken] = useState(null);
  const [verifyTurnstileKey, setVerifyTurnstileKey] = useState(0);
  const [verifyTurnstileSession, setVerifyTurnstileSession] = useState(0);
  const verifyTurnstileSessionRef = useRef(0);
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const inputsRef = useRef([]);

  const displayPendingEmail =
    status?.valid && status.step === "verify_code" && status.pendingEmail
      ? String(status.pendingEmail).trim()
      : "";

  useEffect(() => {
    if (!tokenFromUrl || !isApiConfigured()) {
      setLoadingStatus(false);
      setStatus({ valid: false });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingStatus(true);
      try {
        const s = await getEmailChangeStatus(tokenFromUrl);
        if (!cancelled) setStatus(s);
      } catch {
        if (!cancelled) setStatus({ valid: false });
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenFromUrl]);

  const bumpVerifyTurnstile = () => {
    if (!isTurnstileEnabled() || !isApiConfigured()) return;
    verifyTurnstileSessionRef.current += 1;
    setVerifyTurnstileSession(verifyTurnstileSessionRef.current);
    setVerifyTurnstileKey((k) => k + 1);
    setVerifyTurnstileToken(null);
  };

  const handleVerifyTurnstileChange = (token, widgetSessionId) => {
    if (typeof widgetSessionId === "number" && widgetSessionId !== verifyTurnstileSessionRef.current) {
      return;
    }
    setVerifyTurnstileToken(token);
    if (token) {
      setVerifyError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
    }
  };

  const updateDigit = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    setVerifyError("");

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    event.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i += 1) {
      next[i] = pasted[i];
    }
    setDigits(next);
    setVerifyError("");
    const focusIndex = Math.min(pasted.length, 5);
    inputsRef.current[focusIndex]?.focus();
  };

  const refreshStatus = async () => {
    if (!tokenFromUrl || !isApiConfigured()) return;
    const s = await getEmailChangeStatus(tokenFromUrl);
    setStatus(s);
  };

  const handleSubmitNewEmail = async (event) => {
    event.preventDefault();
    setSubmitError("");
    if (!tokenFromUrl) {
      setSubmitError("This page needs a valid link from your email.");
      return;
    }
    const trimmed = String(newEmail || "").trim().toLowerCase();
    if (!trimmed) {
      setSubmitError("Enter your new email address.");
      return;
    }

    if (!isApiConfigured()) {
      setSubmitError("The app is not connected to the API.");
      return;
    }

    setSubmittingNew(true);
    try {
      if (isTurnstileEnabled() && !newEmailTurnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && newEmailTurnstileToken ? String(newEmailTurnstileToken) : undefined;
      await postEmailChangeNewEmail({
        token: tokenFromUrl,
        newEmail: trimmed,
        turnstileToken: turnstilePayload,
      });
      setResendStatus("");
      setNewEmailTurnstileKey((k) => k + 1);
      setNewEmailTurnstileToken(null);
      await refreshStatus();
      setDigits(["", "", "", "", "", ""]);
      bumpVerifyTurnstile();
    } catch (err) {
      const raw = err instanceof Error ? err.message : "Something went wrong";
      setSubmitError(formatTurnstileErrorForUser(raw));
      setNewEmailTurnstileKey((k) => k + 1);
      setNewEmailTurnstileToken(null);
    } finally {
      setSubmittingNew(false);
    }
  };

  const handleVerifySubmit = async (event) => {
    event.preventDefault();
    const code = digits.join("");
    if (code.length !== 6) {
      setVerifyError("Enter the full 6-digit code.");
      return;
    }
    if (!tokenFromUrl) {
      setVerifyError("This page needs a valid link from your email.");
      return;
    }

    setVerifyError("");
    setResendStatus("");
    setSubmittingVerify(true);
    try {
      if (isTurnstileEnabled() && !verifyTurnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && verifyTurnstileToken ? String(verifyTurnstileToken) : undefined;
      await postEmailChangeVerify({
        token: tokenFromUrl,
        code,
        turnstileToken: turnstilePayload,
        authToken,
      });
      if (typeof onSignIn === "function") {
        onSignIn(null, { token: null });
      }
      setDone(true);
      bumpVerifyTurnstile();
    } catch (err) {
      const raw = err instanceof Error ? err.message : "Verification failed";
      setVerifyError(formatTurnstileErrorForUser(raw));
      bumpVerifyTurnstile();
    } finally {
      setSubmittingVerify(false);
    }
  };

  const handleResend = async () => {
    setVerifyError("");
    setResendStatus("");
    setSubmittingVerify(true);
    try {
      if (isTurnstileEnabled() && !verifyTurnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && verifyTurnstileToken ? String(verifyTurnstileToken) : undefined;
      await postEmailChangeResend({
        token: tokenFromUrl,
        turnstileToken: turnstilePayload,
      });
      setResendStatus(
        "We sent a new code. Complete the security check again below, then enter the code."
      );
      bumpVerifyTurnstile();
    } catch (err) {
      const raw = err instanceof Error ? err.message : "Could not resend";
      setVerifyError(formatTurnstileErrorForUser(raw));
      bumpVerifyTurnstile();
    } finally {
      setSubmittingVerify(false);
    }
  };

  const showEnterEmail = Boolean(
    !loadingStatus && status?.valid && status.step === "enter_email" && !done
  );
  const showVerify = Boolean(
    !loadingStatus && status?.valid && status.step === "verify_code" && !done
  );
  const showInvalid =
    !loadingStatus && (!status || !status.valid) && !done && tokenFromUrl && isApiConfigured();
  const showMissingToken = !tokenFromUrl && !done;

  return (
    <div className="signup-page jw-public-page jw-auth-page">
      <PageSeo
        title="Change email address"
        description="Update the email address linked to your JOBUWIN account."
        path="/old1/change-email"
        noindex
      />
      <nav className="landing-nav jw-public-nav" aria-label="Change email navigation">
        <div className="landing-nav-shell jw-public-nav__shell">
          <SiteLogo />
          <div className="nav-buttons landing-nav-actions">
            <button type="button" className="jw-public-button jw-public-button--text landing-nav-login" onClick={() => navigate("/signin")}>
              Sign in
            </button>
          </div>
        </div>
      </nav>

      <main id="main-content" className="signup-container jw-auth-shell">
        <aside className="jw-auth-aside" aria-label="Email change guidance">
          <span className="jw-public-chip">
            <ShieldCheck className="jw-public-icon" aria-hidden="true" />
            Secure account change
          </span>
          <h1>Update your sign-in email safely.</h1>
          <p>
            Email changes use a secure link and a verification code so your account remains protected.
          </p>
          <div className="jw-auth-benefits">
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Secure link validation</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Code sent to the new email</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Sign-in refresh after completion</span>
          </div>
        </aside>

        <section className="signup-card jw-public-card jw-auth-card">
          <Link className="back-link jw-public-back-link" to="/signin">
            <ArrowLeft className="jw-public-icon" aria-hidden="true" />
            Back to sign in
          </Link>
          <p className="eyebrow jw-public-eyebrow">Change email</p>
          {loadingStatus ? (
            <JobuwinPageLoading
              compact
              title="Loading…"
              subtitle="Validating your secure link."
            />
          ) : (
            <h2>
              {done
                ? "Email updated"
                : showVerify
                  ? "Confirm your new email"
                  : "Enter your new email"}
            </h2>
          )}

          {showMissingToken && !loadingStatus ? (
            <div role="alert">
              <p className="subtitle">
                This page needs a valid link from your email. Request a change from your profile after signing in.
              </p>
              <p className="verification-help" style={{ marginTop: "1rem" }}>
                <Link className="change-email-link" to="/applicant-profile">
                  Go to profile
                </Link>
              </p>
            </div>
          ) : null}

          {!isApiConfigured() && !done ? (
            <p className="subtitle" role="alert">
              The app is not connected to the API. Email change is unavailable in this mode.
            </p>
          ) : null}

          {showInvalid ? (
            <div role="alert">
              <p className="subtitle">
                This link is invalid or has expired. Request a new email change from your JOBUWIN profile.
              </p>
              <p className="verification-help" style={{ marginTop: "1rem" }}>
                <Link className="change-email-link" to="/applicant-profile">
                  Go to profile
                </Link>
              </p>
            </div>
          ) : null}

          {done ? (
            <>
              <p className="subtitle">
                Your sign-in email was updated. For security, you have been signed out on all devices. Please sign in
                again using your new email address.
              </p>
              <p className="verification-help" style={{ marginTop: "1rem" }}>
                <Link className="change-email-link" to="/signin">
                  Go to sign in
                </Link>
              </p>
            </>
          ) : null}

          {showEnterEmail ? (
            <>
              <p className="subtitle">
                Enter the email address you want to use for your account. After you continue, we will send a 6-digit
                code to that address. You cannot switch to another email on this page afterward; request a new change
                link from your profile if you need a different address.
              </p>
              <form id="jobuwin-change-email-new-form" className="signup-form jw-auth-form" onSubmit={handleSubmitNewEmail}>
                <label htmlFor="changeEmailNew">New email address</label>
                <div className="jw-auth-field">
                  <Mail className="jw-public-icon" aria-hidden="true" />
                  <input
                    id="changeEmailNew"
                    name="changeEmailNew"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                  />
                </div>

                {!(isTurnstileEnabled() && isApiConfigured()) && submitError ? (
                  <p className="form-inline-alert-danger" role="alert">
                    {submitError}
                  </p>
                ) : null}

                {!(isTurnstileEnabled() && isApiConfigured()) ? (
                  <button type="submit" className="submit-button jw-public-button jw-public-button--primary" disabled={submittingNew}>
                    {submittingNew ? "Sending..." : "Continue"}
                    <ArrowRight className="jw-public-icon" aria-hidden="true" />
                  </button>
                ) : null}
              </form>

              {isTurnstileEnabled() && isApiConfigured() ? (
                <div className="signup-form-captcha-sibling">
                  {submitError === TURNSTILE_REQUIRED_MESSAGE ? (
                    <p className="turnstile-inline-alert" role="alert">
                      {submitError}
                    </p>
                  ) : null}
                  <TurnstileField
                    tokenSessionId={0}
                    onTokenChange={(token) => {
                      setNewEmailTurnstileToken(token);
                      if (token) {
                        setSubmitError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
                      }
                    }}
                    widgetKey={`change-email-new-${newEmailTurnstileKey}`}
                    options={{ action: "change-email-new" }}
                  />
                  <p className="turnstile-human-note">
                    Security check: making sure you are a genuine human :-)
                  </p>
                  {submitError && submitError !== TURNSTILE_REQUIRED_MESSAGE ? (
                    <p className="form-inline-alert-danger" role="alert">
                      {submitError}
                    </p>
                  ) : null}
                  <button
                    type="submit"
                    form="jobuwin-change-email-new-form"
                    className="submit-button jw-public-button jw-public-button--primary"
                    disabled={submittingNew}
                  >
                    {submittingNew ? "Sending..." : "Continue"}
                    <ArrowRight className="jw-public-icon" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </>
          ) : null}

          {showVerify ? (
            <>
              <p className="subtitle">
                We sent a 6-digit code to <strong>{displayPendingEmail}</strong>. Enter it below to finish updating
                your email.
              </p>
              <p className="subtitle" style={{ fontSize: "0.95rem", color: "#4b5563" }}>
                To use a different email address, complete this step or request a new email change link from your
                profile.
              </p>

              <form className="signup-form jw-auth-form jw-auth-code-form" id="jobuwin-change-email-verify-form" onSubmit={handleVerifySubmit}>
                <label htmlFor="change-code-0">Verification code</label>
                <div className="jw-auth-code-heading">
                  <MailCheck className="jw-public-icon" aria-hidden="true" />
                  Enter the email code
                </div>
                <div className="code-inputs" onPaste={handlePaste}>
                  {digits.map((digit, index) => (
                    <input
                      key={`ce-digit-${index}`}
                      id={`change-code-${index}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={digit}
                      onChange={(event) => updateDigit(index, event.target.value)}
                      onKeyDown={(event) => handleKeyDown(index, event)}
                      ref={(element) => {
                        inputsRef.current[index] = element;
                      }}
                    />
                  ))}
                </div>

                {resendStatus ? <p className="verification-help">{resendStatus}</p> : null}

                {!(isTurnstileEnabled() && isApiConfigured()) && verifyError ? (
                  <p className="form-inline-alert-danger" role="alert">
                    {verifyError}
                  </p>
                ) : null}

                {!(isTurnstileEnabled() && isApiConfigured()) ? (
                  <button type="submit" className="submit-button jw-public-button jw-public-button--primary" disabled={submittingVerify}>
                    {submittingVerify ? "Verifying..." : "Confirm new email"}
                    <ArrowRight className="jw-public-icon" aria-hidden="true" />
                  </button>
                ) : null}
              </form>

              {isTurnstileEnabled() && isApiConfigured() ? (
                <div className="signup-form-captcha-sibling">
                  {verifyError === TURNSTILE_REQUIRED_MESSAGE ? (
                    <p className="turnstile-inline-alert" role="alert">
                      {verifyError}
                    </p>
                  ) : null}
                  <TurnstileField
                    tokenSessionId={verifyTurnstileSession}
                    onTokenChange={handleVerifyTurnstileChange}
                    widgetKey={`change-email-verify-${verifyTurnstileKey}`}
                    options={{ action: "change-email-verify" }}
                  />
                  <p className="turnstile-human-note">
                    Security check: making sure you are a genuine human :-)
                  </p>
                  {verifyError && verifyError !== TURNSTILE_REQUIRED_MESSAGE ? (
                    <p className="form-inline-alert-danger" role="alert">
                      {verifyError}
                    </p>
                  ) : null}
                  <button
                    type="submit"
                    form="jobuwin-change-email-verify-form"
                    className="submit-button jw-public-button jw-public-button--primary"
                    disabled={submittingVerify}
                  >
                    {submittingVerify ? "Verifying..." : "Confirm new email"}
                    <ArrowRight className="jw-public-icon" aria-hidden="true" />
                  </button>
                </div>
              ) : null}

              <p className="verification-help" style={{ marginTop: "12px" }}>
                <button
                  type="button"
                  className="change-email-link"
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
                  disabled={submittingVerify}
                  onClick={handleResend}
                >
                  Resend code
                </button>
              </p>
            </>
          ) : null}
        </section>
      </main>

      <old1_PublicSiteFooter />
    </div>
  );
}

export default old1_ChangeEmailPage;
